/**
 * Automated Emails: constants and pure helpers shared by mail.vue and
 * previewModal.vue. Nothing here touches Vue state.
 */

import { hostDomain } from "@/code/service";

/**
 * The template types the backend keeps, keyed the way every API call names them. "custom"
 * templates are never in use: a ticket's Send e-mail action names one by its ID (EMAIL_ACTIONS_PLAN.md 2).
 */
export type MailGroup = "confirmation" | "welcome" | "verification" | "forgot_password" | "invitation" | "newsletter_subscription" | "custom";

/** One received template, as Service.getMailTemplates maps it (mid/subj/stmp/url). */
export type MailTemplate = {
    message_id: string;
    subject: string;
    timestamp: number;
    url: string;
};

/** Columns get-templates can sort on. Every key here needs a DEFAULT_SEARCH entry. */
export type SortKey = "timestamp" | "subject";

export type FillContext = {
    email: string;
    name: string;
    serviceName: string;
};

export const MAIL_DOMAIN = "mail.skapi.com";

// ─── e-mail bodies ───
/** Fetched bodies by address: a template or newsletter shown once is not fetched again. */
const htmlCache = new Map<string, string>();

/** Forget every fetched body (the Automated Emails page's refresh). */
export const clearHtmlCache = () => htmlCache.clear();

/** The HTML behind a template's or newsletter's address, for the preview dialog. Throws when the fetch fails. */
export async function loadHtml(url: string): Promise<string> {
    const hit = htmlCache.get(url);
    if (hit !== undefined) return hit;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const html = await res.text();
    htmlCache.set(url, html);
    return html;
}


/**
 * `label` names the type in dialogs and toasts; `tab` is the short name on the tab
 * itself, so the tabs fit on one row of the content column (the full names wrapped
 * "Newsletter Confirmation" onto a row of its own at every desktop width).
 */
export const MAIL_TABS: { group: MailGroup; label: string; tab: string }[] = [
    { group: "confirmation", label: "Signup Confirmation", tab: "Signup" },
    { group: "welcome", label: "Welcome Email", tab: "Welcome" },
    { group: "verification", label: "Verification Email", tab: "Verification" },
    // the forgot password e-mail (the password reset code): its own kind since 2026-10, with its own setter and default
    { group: "forgot_password", label: "Forgot Password Email", tab: "Forgot password" },
    { group: "invitation", label: "Invitation Email", tab: "Invitation" },
    { group: "newsletter_subscription", label: "Newsletter Confirmation", tab: "Newsletter" },
    { group: "custom", label: "Custom Email", tab: "Custom" },
];

/** The tab that is never "in use": its templates are sent by a ticket's Send e-mail action. */
export const isCustomGroup = (group: MailGroup): group is "custom" => group === "custom";

/** "local@domain" as [local, "@domain"], so the domain can be kept on one line. */
export const splitAddress = (address: string): [string, string] => {
    const at = address.lastIndexOf("@");
    return at < 0 ? [address, ""] : [address.slice(0, at), address.slice(at)];
};

/**
 * A local part cut after every ".", so the template setter "tp<md5>.<service id>" can
 * wrap at its dot (a <wbr> goes between the pieces). The older "<service id>-tp<md5>"
 * has no dot and comes back whole: it already wraps at its "-".
 */
export const localBreaks = (local: string): string[] => local.match(/[^.]*\.|[^.]+$/g) || [local];

/** The service record keeps the template in use under `template_<group>` as { url, subject }. */
export const templateKey = (group: MailGroup) => "template_" + group;

/**
 * The link placeholder as this environment shows it: https://link.skapi.com, or
 * https://link.skapi.app on dev. The backend accepts both, and the original
 * https://link.skapi, everywhere; the ".com"/".app" forms read as real URLs, so mail
 * editors accept them as a link target.
 */
export const LINK_PLACEHOLDER = `https://link.${hostDomain === "skapi.app" ? "skapi.app" : "skapi.com"}`;

/**
 * Every form the backend replaces with the real link (infra mail/process_mail/template_rules.js
 * uses the same pattern), so the preview fills a template exactly as a sent e-mail would be.
 */
const LINK_PLACEHOLDER_PATTERN = /https:\/\/link\.skapi(?:\.com|\.app)?\/?(?=$|[^\w.-]|\.(?:$|[^\w-]))/g;

/**
 * Placeholders the backend substitutes per template type. A template missing a required
 * one is rejected when it arrives, and the sender gets an e-mail listing what was missing.
 */
export const PLACEHOLDERS: Record<MailGroup, { required: string[]; optional: string[] }> = {
    confirmation: {
        required: [LINK_PLACEHOLDER],
        optional: ["${email}", "${name}", "${service_name}"],
    },
    welcome: {
        required: [],
        optional: ["${email}", "${name}", "${service_name}"],
    },
    verification: {
        required: ["${code}"],
        optional: ["${email}", "${name}", "${service_name}"],
    },
    forgot_password: {
        required: ["${code}"],
        optional: ["${email}", "${name}", "${service_name}"],
    },
    invitation: {
        required: [LINK_PLACEHOLDER, "${email}", "${password}"],
        optional: ["${name}", "${service_name}", "${username}"],
    },
    // the subscription confirmation is sent to an address, not to a user: it carries no ${name}
    newsletter_subscription: {
        required: [LINK_PLACEHOLDER],
        optional: ["${email}", "${service_name}"],
    },
    // plus any ${name} the owner writes, filled by the ticket action that sends it
    custom: {
        required: [],
        optional: ["${email}", "${service_name}"],
    },
};

const LINK_NOTE = "Use it as the URL of a link. The older https://link.skapi still works.";
const USER_NAME = "The user's name from their profile, or empty when they have not set one.";
const SERVICE_NAME = "Your project's name.";

/** What each placeholder turns into, per template type. Shown when a placeholder is hovered or focused. */
export const PLACEHOLDER_DESCRIPTIONS: Record<MailGroup, Record<string, string>> = {
    confirmation: {
        [LINK_PLACEHOLDER]: `The signup confirmation link. The user opens it to activate their account. ${LINK_NOTE}`,
        "${email}": "The email address the user signed up with.",
        "${name}": USER_NAME,
        "${service_name}": SERVICE_NAME,
    },
    welcome: {
        "${email}": "The user's email address.",
        "${name}": USER_NAME,
        "${service_name}": SERVICE_NAME,
    },
    verification: {
        "${code}": "The one-time verification code the user enters.",
        "${email}": "The email address being verified.",
        "${name}": USER_NAME,
        "${service_name}": SERVICE_NAME,
    },
    forgot_password: {
        "${code}": "The one-time password reset code.",
        "${email}": "The login email the code is sent to.",
        "${name}": USER_NAME,
        "${service_name}": SERVICE_NAME,
    },
    invitation: {
        [LINK_PLACEHOLDER]: `The link the invited user opens to accept the invitation. Without it they cannot sign in. ${LINK_NOTE}`,
        "${email}": "The email address the invitation was sent to.",
        "${password}": "The temporary password generated for the invited user.",
        "${name}": "The name given in the invitation, or empty when none was given.",
        "${service_name}": SERVICE_NAME,
        "${username}": "The login username the user was invited with, or their email address when they have none. A resent invitation shows the same, except one first sent before usernames were kept for resending, which shows the email address.",
    },
    newsletter_subscription: {
        [LINK_PLACEHOLDER]: `The link the subscriber opens to confirm the subscription. ${LINK_NOTE}`,
        "${email}": "The email address being subscribed.",
        "${service_name}": SERVICE_NAME,
    },
    custom: {
        "${email}": "The address the ticket's Send e-mail action sends to.",
        "${service_name}": SERVICE_NAME,
    },
};

/**
 * When the backend sends each type, shown under the Current template value. Text
 * between backticks is an SDK name and is set as code (see codeParts).
 */
export const SEND_TRIGGERS: Record<MailGroup, string> = {
    confirmation: "Sent on signup with `signup_confirmation` on, or by `resendSignupConfirmation()`, with the activation link.",
    welcome: "Sent at a user's first login if their email is already verified (confirmed signup, accepted invite, OpenID).",
    verification: "Sent with a one-time code by `verifyEmail()` for an unverified email. A phone number is verified by the SMS text below instead.",
    forgot_password: "Sent with a one-time code by `forgotPassword()`, always by email, to the account's login email.",
    invitation: "Sent by `inviteUser()` and `resendInvitation()`, with the generated password and acceptance link.",
    newsletter_subscription: "Sent when a logged-out visitor subscribes to a public newsletter, with a link to confirm it.",
    custom: "Sent by a ticket's `mail` action (Send e-mail), which names the template by its ID, the recipient, and the values of every other `${name}` the template carries.",
};

/** "a `b` c" as ["a ", "b", " c"]: the odd indexes are the backticked (code) parts. */
export const codeParts = (text: string): string[] => text.split("`");

/** Subject the backend uses when no template has been set for the type. */
export const DEFAULT_SUBJECTS: Record<MailGroup, string> = {
    confirmation: "[${service_name}] Signup Confirmation",
    welcome: "Thank you for joining ${service_name}",
    verification: "[${service_name}] Verification code",
    forgot_password: "[${service_name}] Password reset code",
    invitation: "[${service_name}] Invitation",
    newsletter_subscription: "[${service_name}] Thank you for subscribing to our newsletter.",
    // a custom template has no built-in default: nothing is sent without one
    custom: "",
};

/** Body the backend sends when no template has been set for the type. */
export const DEFAULT_HTML: Record<MailGroup, string> = {
    confirmation: `<pre style="line-height: 1.4;">
Please activate your account by clicking this <a href="https://link.skapi" style="font-weight: bold">LINK</a>
Your activation link is valid for 7 days.
</pre>`,
    welcome: `<pre style="line-height: 1.4;">
<span style="font-weight: bold">Hello \${name}</span>
Thank you for joining \${service_name}
Your login email is: <span style="font-weight: bold">\${email}</span></pre>`,
    verification: `<pre style="line-height: 1.4;">Your verification code is <span style="font-weight: bold">\${code}</span></pre>`,
    forgot_password: `<pre style="line-height: 1.4;">Your password reset code is <span style="font-weight: bold">\${code}</span></pre>`,
    invitation: `<pre style="line-height: 1.4;">
Hello \${name}
You are invited to \${service_name}
You can accept the invitation by clicking on this <a href="https://link.skapi" style="font-weight: bold">LINK</a>

Your login e-mail is: <b>\${email}</b>
Your account password is: <b>\${password}</b>

Your activation link is valid for 7 days.
</pre>`,
    newsletter_subscription: `<pre style="line-height: 1.4;">
Thank you for subscribing to \${service_name} newsletter.
Please confirm your subscription by clicking this <a href="https://link.skapi" style="font-weight: bold">LINK</a>
</pre>`,
    custom: "",
};

const replaceAll = (text: string, needle: string, replacement: string) => text.split(needle).join(replacement);

// ---------------------------------------------------------------------------
// the phone verification SMS: a project setting (template_sms_verification), not a template
// ---------------------------------------------------------------------------

/** The text the backend sends when the setting is empty (admin/cognito/custom_messages). */
export const SMS_DEFAULT_TEXT = "[${service_name}] Your verification code is ${code}";
/** One SMS segment, as the setter measures it. */
export const SMS_MAX_LENGTH = 160;
/** Characters as the backend counts them: code points, not UTF-16 units. */
export const smsLength = (text: string): number => Array.from(text || "").length;

/**
 * Why the SMS text is refused, as sentences for the owner; [] when it is fine. An empty
 * text is fine: it puts the built-in default back. Checked here before the request, and
 * again by the setter with the same rules.
 */
export function smsProblems(text: string): string[] {
    if (!text) return [];
    const out: string[] = [];
    if (!text.includes("${code}")) out.push("The text needs ${code}: that is where the verification code goes.");
    const length = smsLength(text);
    if (length > SMS_MAX_LENGTH) out.push(`The text is ${length} characters; up to ${SMS_MAX_LENGTH} are sent.`);
    return out;
}

/** Default value/condition per sort key: "everything", ordered by that key. */
export const DEFAULT_SEARCH: { [key in SortKey]: { value: number | string; condition: ">=" | ">" } } = {
    timestamp: { value: 0, condition: ">=" },
    subject: { value: " ", condition: ">" },
};

// A template written in a mail app (Gmail, Outlook) carries no colours of its own: plain text
// in <font> tags, in a document that is transparent. Behind a dark page (the dashboard's dark
// theme, a dark-mode extension) its default black text sat on a dark backdrop and the preview
// looked blank, while templates that set their own white background showed. Mail clients render
// on white, so the preview document gets a light base of its own, before anything the template
// says. It goes after a leading doctype, so a template that has one keeps standards mode.
export const PREVIEW_BASE = "<style>html{background:#fff;color:#000;color-scheme:light}</style>";

/** The template body as the preview frame shows it: on white, in black, then the template. */
export function previewDocument(html: string): string {
    const doctype = /^\s*<!doctype[^>]*>/i.exec(html);
    return doctype ? html.slice(0, doctype[0].length) + PREVIEW_BASE + html.slice(doctype[0].length) : PREVIEW_BASE + html;
}

/** Swap every placeholder for a sample value, the way the preview toggle does. */
export function fillPlaceholders(text: string, ctx: FillContext): string {
    if (!text) return text;
    let out = text;
    out = replaceAll(out, "${code}", "123456");
    // the backend sends the e-mail address when the user has no login username
    out = replaceAll(out, "${username}", ctx.email);
    out = replaceAll(out, "${email}", ctx.email);
    out = replaceAll(out, "${name}", ctx.name);
    out = replaceAll(out, "${service_name}", ctx.serviceName);
    out = replaceAll(out, "${password}", "abc123&&");
    out = out.replace(LINK_PLACEHOLDER_PATTERN, "https://path.to/link");
    return out;
}
